import React, { useMemo } from "react";

import blocks, { BlockType } from "../../..";

import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import SelectWorkflow from "@/components/ui/SelectWorkflow";
import { useUser } from "@/context/user-context";
import { Entity, EntityType } from "@/queries";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";

interface WorkflowSelectionSectionProps {
  workflowName?: string;
  workflowId?: number | string;
  workflowVersionNumber?: number;
  workflowLabelName?: string;
  folderId?: number | null;
  isLoading?: boolean;
  onWorkflowSelection: (entity: Entity<EntityType.WORKFLOW>) => void;
  hasError?: unknown;
}

const WorkflowSelectionSection = React.memo(
  ({
    workflowName,
    workflowId,
    workflowVersionNumber,
    workflowLabelName,
    folderId,
    isLoading = false,
    onWorkflowSelection,
    hasError,
  }: WorkflowSelectionSectionProps) => {
    const user = useUser();
    const workspaceId = user?.activeWorkspaceId;
    const WorkflowIcon = useMemo(() => blocks[BlockType.WORKFLOW].icon!, []);
    const readonly = useIsReadonly();

    // Type guard for extracting error message
    const getErrorMessage = (error: unknown): string => {
      if (error && typeof error === "object" && "message" in error) {
        const { message } = error as { message: unknown };
        if (typeof message === "string") {
          return message;
        }
      }
      return "Unable to load the selected workflow version. Please select a different version or check if the workflow still exists.";
    };

    const workflowUrl = React.useMemo(() => {
      const id = typeof workflowId === "string" ? Number(workflowId) : workflowId;
      if (!id || !workspaceId) {
        return undefined;
      }

      let url = `/workspace/${workspaceId}/workflow/${id}`;

      if (workflowVersionNumber) {
        url += `/version/${workflowVersionNumber}`;
      }

      return url;
    }, [workflowId, workspaceId, workflowVersionNumber]);

    const workflowIdNumber = typeof workflowId === "string" ? Number(workflowId) : workflowId;

    return (
      <SectionGroup
        title="Workflow Selection"
        description="Choose and configure your workflow"
        icon={<WorkflowIcon className="h-4 w-4" />}
      >
        <div className="space-y-4">
          <SelectWorkflow
            workflow={
              workflowIdNumber
                ? {
                    id: workflowIdNumber,
                    name: workflowName!,
                    folder_id: folderId ?? undefined,
                    version_number: workflowVersionNumber,
                    label: workflowLabelName,
                  }
                : undefined
            }
            selectLatest={false}
            onSelect={onWorkflowSelection}
            readonly={readonly}
            isLoading={isLoading}
            url={workflowUrl}
            hasError={!!hasError}
            errorMessage={hasError ? getErrorMessage(hasError) : undefined}
          />
        </div>
      </SectionGroup>
    );
  },
);

WorkflowSelectionSection.displayName = "WorkflowSelectionSection";

export default WorkflowSelectionSection;
